import { Injectable } from '@angular/core';
import { BundleBuilderApiService } from '../../cart/bundle-builder/bundle-builder-api.service';
import {
  BundleSet,
  CartData,
  Item,
  ItemDiscountTalonOneItem,
  ProductItem,
  TalonOneItem,
  TalonOneWebCode,
} from '../models/cart-data.model';
import { CheckoutData } from '../models/checkout-data.model';

@Injectable({
  providedIn: 'root',
})
export class CartParserService {

  constructor(private api: BundleBuilderApiService) {

  }

  calculateTotalVouchers(flatList: Item[]): number {
    const total = flatList
      .filter((n) => n.Webcode === TalonOneWebCode.T1VOUCHER)
      .reduce((sum, p) => sum + Math.abs(p.UnitPrice * p.Quantity), 0);
    return total;
  }

  parseCartItems(flatList: Item[], bundleId: number): ProductItem[] {
    //stackoverflow.com/questions/32496912/parent-child-relation-sorting-in-javascript/53759112
    const products = (flatList.filter((n) => !n.IsPromotion) as ProductItem[])
      .filter((n) => n.Attributes.BundleId == bundleId);

    let anchorItemsTotal = 0;
    if (bundleId) {
      products.forEach((item) => {
        if (item.Attributes.IsBundleAnchor) {
          anchorItemsTotal += 1;
        }
      });
    }

    const talonOneLineItemDiscounts = flatList.filter(
      (n) => n.Webcode == TalonOneWebCode.T1LDISCOUNT || n.Webcode == TalonOneWebCode.T1LREBATE
    ) as ItemDiscountTalonOneItem[];

    for (let product of products) {
      product.DiscountItems = talonOneLineItemDiscounts.filter((i) => i.ParentRecordID === product.RecordID);

      let talonOneDiscounts = 0;
      
      product.DiscountItems.forEach(discount => {
        // if product quantity and discount quantity differ, then discount qty takes priority.
        // this would happen especially on free items, only the 1st item is free.
        const qty = discount.Quantity != product.Quantity ? 
          discount.Quantity : 
          product.Quantity;

          talonOneDiscounts += Math.abs(discount.UnitPrice) * qty;
      });

      let databaseDiscounts = 0;

      databaseDiscounts = product.Quantity * (product.Attributes.RegularPrice - product.UnitPrice); 

      product.DiscountTotal = (talonOneDiscounts + databaseDiscounts);

      product.LineTotal = product.Attributes.RegularPrice * product.Quantity - Math.abs(product.DiscountTotal);

      product.IsLastAnchorItem = product.Attributes.IsBundleAnchor && anchorItemsTotal === 1;
    }

    let parents = products.filter((n) => !n.Attributes.AccessoryParent);
    //collecting all children
    let children = products.filter((n) => n.Attributes.AccessoryParent);

    //iterating through children and pushing it to **parents** json just below the parent
    for (var child of children) {
      let index = parents.findIndex((obj) => obj.Webcode === child.Attributes.AccessoryParent);
      parents.splice(index + 1, 0, child);
    }
    return parents;
  }

  public consolidateDiscountRebates(flatList: Item[]): Item[] {
    const lineItemDiscounts = flatList.filter(
      (n) => n.Webcode == TalonOneWebCode.T1LDISCOUNT || n.Webcode == TalonOneWebCode.T1LREBATE
    ) as ItemDiscountTalonOneItem[];

    let products = flatList as ProductItem[];
    const productsToRemove = [];

    for (let product of products) {
      product.DiscountItems = lineItemDiscounts.filter((i) => i.ParentRecordID === product.RecordID);
      
      const t1Rebates = product.DiscountItems.filter((n) => n.Webcode === TalonOneWebCode.T1LREBATE);
      
      for(var t1Rebate of t1Rebates) {
        const associatedDiscount = product.DiscountItems.find(x =>
          x.Webcode === TalonOneWebCode.T1LDISCOUNT &&
          Boolean((x as any).Attributes?.RebateLinkingId) && Boolean((t1Rebate as any).Attributes?.RebateLinkingId) &&
          (x as any).Attributes.RebateLinkingId === (t1Rebate as any).Attributes.RebateLinkingId
        );

        // remove the rebate && add the rebate amount to the discount to simplify the UI
        const rebateIndex = product.DiscountItems.findIndex(x => x.RecordID === t1Rebate.RecordID);

        if (associatedDiscount && rebateIndex > -1) {
          product.DiscountItems.splice(rebateIndex, 1);
          associatedDiscount.UnitPrice += t1Rebate.UnitPrice;

          productsToRemove.push(t1Rebate.RecordID);
        }
      }
    }

    products = products.filter(x => !productsToRemove.includes(x.RecordID));

    return products as Item[];
  }

  parseBundleSets(flatList: Item[]): BundleSet[] {
    const bundleSets: BundleSet[] = [];
    const products = (flatList.filter((n) => !n.IsPromotion) as ProductItem[]);

    const uniqueBundleIds = [...new Set(products.filter((n) => n.Attributes.BundleId != null).map(item => item.Attributes.BundleId))];

    for (let bundleId of uniqueBundleIds) {
      let bundle = {} as BundleSet;
      bundle.BundleId = bundleId;

      bundle.Items = this.parseCartItems(flatList, bundleId);
      bundle.Items.sort((a, b) => b.UnitPrice - a.UnitPrice);
      bundle.ItemsCount = bundle.Items.length;
      bundle.Total = bundle.Items.reduce((sum, current) => sum + current.LineTotal, 0);
      bundle.TotalSavings = bundle.Items.reduce((sum, current) => sum + Math.abs(current.DiscountTotal), 0);
      bundleSets.push(bundle);
    }

    return bundleSets;
  }

  public initCheckoutData(data: CheckoutData) {
    this.initCartOrCheckoutData(data);
  }

  public initCartData(data: CartData) {
    this.initCartOrCheckoutData(data);
  }

  private initCartOrCheckoutData(data: CartData | CheckoutData) {
    if (!data?.CartItems || !data.CartItems.length) {
      data.itemCount = 0;
      return;
    }

    let flatItems = ([] as Item[]).concat(...data.CartItems);
    flatItems = this.consolidateDiscountRebates(flatItems);

    data.UnBundledItems = this.parseCartItems(flatItems, null);
    data.BundleSets = this.parseBundleSets(flatItems);
    data.itemCount = data.UnBundledItems.length + data.BundleSets.reduce((sum, current) => sum + current.Items.length, 0);

    data.OrderLevelPromotions = flatItems.filter((n) => n.IsOrderLevelPromotion) as TalonOneItem[];

    let bundleSavings = 0;
    data.BundleSets.map(x => x.Items).forEach(x => bundleSavings += x.reduce((sum, current) => sum + Math.abs(current.DiscountTotal), 0));

    const unbundledSavings = data.UnBundledItems.reduce((sum, current) => sum + Math.abs(current.DiscountTotal), 0);

    data.CartSummary.unbundledSubtotal = data.UnBundledItems.reduce((sum, current) => sum + current.LineTotal, 0);
    data.CartSummary.unbundledSavings = unbundledSavings;
    data.CartSummary.totalSavings = bundleSavings + unbundledSavings;
  }
}
